import { useCallback, useState } from 'react'

/**
 * Marca por linha nas telas que sugerem e deixam a pessoa corrigir: a escolha
 * dela vence, senão vale a marca que veio com a linha (`resolveDefault`).
 * Linha tocada deixa de contar como sugestão, mesmo se a marca coincidir.
 */
export const useBrandAssignments = <T>(
  getId: (row: T) => string,
  resolveDefault: (row: T) => string | null
) => {
  const [overrides, setOverrides] = useState<Record<string, string | null>>({})

  const isTouched = (row: T) => getId(row) in overrides

  const brandFor = (row: T) =>
    isTouched(row) ? overrides[getId(row)] : resolveDefault(row)

  /** Sugestão mantida sem revisão grava como tal: é o mesmo estado do tom roxo. */
  const isSuggested = (row: T) => !isTouched(row) && !!resolveDefault(row)

  const assign = (row: T, brand: string | null) =>
    setOverrides(previous => ({ ...previous, [getId(row)]: brand }))

  const reset = useCallback(() => setOverrides({}), [])

  return { brandFor, isTouched, isSuggested, assign, reset }
}

/**
 * Uma request por marca e origem: sugestão aceita como veio e escolha manual
 * chegam à API com `source` distinto, mesmo apontando para a mesma marca.
 */
export const buildMoveRequests = <T>(
  byBrand: Record<string, T[]>,
  toIdentities: (rows: T[]) => AdvertiserBrandIdentities,
  isSuggested: (row: T) => boolean
): MoveAdvertiserBrandProductsRequest[] => {
  return Object.entries(byBrand).flatMap(([brand, rows]) => {
    const suggested = rows.filter(isSuggested)
    const manual = rows.filter(row => !isSuggested(row))

    const requests: MoveAdvertiserBrandProductsRequest[] = []

    if (suggested.length) {
      requests.push({
        brand,
        source: 'system_suggestion',
        ...toIdentities(suggested)
      })
    }

    if (manual.length) {
      requests.push({ brand, source: 'manual', ...toIdentities(manual) })
    }

    return requests
  })
}

/** Agrupa pela marca resolvida; linha sem marca fica em `unresolved`. */
export const groupRowsByBrand = <T>(
  rows: T[],
  brandFor: (row: T) => string | null
) => {
  // nome de marca é texto livre e vira chave: mapa sem protótipo
  const byBrand: Record<string, T[]> = Object.create(null)
  const unresolved: T[] = []

  rows.forEach(row => {
    const brand = brandFor(row)

    if (!brand) {
      unresolved.push(row)

      return
    }

    byBrand[brand] = [...(byBrand[brand] ?? []), row]
  })

  return { byBrand, unresolved }
}
